import { useEffect, useMemo, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import {
  CustomTextField,
  DialogActions,
  DialogBody,
  DialogContainer,
  DialogTitle,
  HorizontalForm,
  VerticalForm,
} from '../../../common/components';
import { useUpdateNotificationConfig } from '../../../hooks/useNotifications';

const COMPLETED_PROJECT_RULE_KEY = 'completed_project_contracts_missing_operational_data';
const COUPON_DUE_RULE_KEY = 'coupon_due_tomorrow';
const COUPON_COLLECTION_RULE_KEY = 'coupon_collection_by_due';

const pad = (value) => String(value).padStart(2, '0');

const parseScheduleNumber = (value, fallback, minimum, maximum) => {
  const numericValue = Number(value);
  const parsed = Number.isFinite(numericValue) ? numericValue : fallback;
  return Math.min(Math.max(parsed, minimum), maximum);
};

const scheduleTimeFields = (scheduleConfig, fallbackHour, fallbackMinute) => ({
  time: `${pad(scheduleConfig?.hour ?? fallbackHour)}:${pad(scheduleConfig?.minute ?? fallbackMinute)}`,
});

const buildCronExpression = ({ frequency, dayOfMonth, hour, minute }) => {
  if (frequency === 'monthly') {
    return `${minute} ${hour} ${dayOfMonth} * *`;
  }
  return `${minute} ${hour} * * *`;
};

const buildBaseForm = (scheduleConfig, fallbackHour, fallbackMinute) => {
  return scheduleTimeFields(scheduleConfig, fallbackHour, fallbackMinute);
};

const buildInitialForm = (configItem) => {
  const scheduleConfig = configItem?.schedule_config || {};
  const daysAfterDue = scheduleConfig.days_after_due || {};
  const ruleKey = configItem?.rule_key || configItem?.config_key;

  if (ruleKey === COMPLETED_PROJECT_RULE_KEY) {
    return {
      ...buildBaseForm(scheduleConfig, 4, 10),
      dayOfMonth: scheduleConfig.day_of_month || 1,
      daysBeforeDue: 1,
      firstDueDelay: 1,
      secondDueDelay: 1,
      thirdDueDelay: 1,
    };
  }

  if (ruleKey === COUPON_DUE_RULE_KEY) {
    return {
      ...buildBaseForm(scheduleConfig, 8, 0),
      dayOfMonth: 1,
      daysBeforeDue: scheduleConfig.days_before_due ?? 1,
      firstDueDelay: 1,
      secondDueDelay: 1,
      thirdDueDelay: 1,
    };
  }

  return {
    ...buildBaseForm(scheduleConfig, 9, 0),
    dayOfMonth: 1,
    daysBeforeDue: 1,
    firstDueDelay: daysAfterDue['1'] ?? 1,
    secondDueDelay: daysAfterDue['2'] ?? 1,
    thirdDueDelay: daysAfterDue['3'] ?? 1,
  };
};

export const getScheduleSummary = (configItem) => {
  const scheduleConfig = configItem?.schedule_config || {};
  const ruleKey = configItem?.rule_key || configItem?.config_key;
  const hour = pad(scheduleConfig.hour ?? (ruleKey === COUPON_COLLECTION_RULE_KEY ? 9 : 8));
  const minute = pad(scheduleConfig.minute ?? (ruleKey === COMPLETED_PROJECT_RULE_KEY ? 10 : 0));

  if (ruleKey === COMPLETED_PROJECT_RULE_KEY) {
    return `Mensual día ${scheduleConfig.day_of_month || 1}, ${hour}:${minute}`;
  }
  if (ruleKey === COUPON_DUE_RULE_KEY) {
    return `${scheduleConfig.days_before_due ?? 1} día(s) antes, ${hour}:${minute}`;
  }

  const daysAfterDue = scheduleConfig.days_after_due || {};
  return `V1 +${daysAfterDue['1'] ?? 1}d, V2 +${daysAfterDue['2'] ?? 1}d, V3 +${daysAfterDue['3'] ?? 1}d, ${hour}:${minute}`;
};

export const NotificationScheduleDialog = ({ open, configItem, onClose }) => {
  const updateConfig = useUpdateNotificationConfig();
  const [form, setForm] = useState(buildInitialForm(configItem));
  const ruleKey = configItem?.rule_key || configItem?.config_key;

  useEffect(() => {
    if (!open) return;
    setForm(buildInitialForm(configItem));
  }, [open, configItem]);

  const schedulePreview = useMemo(() => {
    const [rawHour, rawMinute] = String(form.time || '').split(':');
    const hour = parseScheduleNumber(rawHour, 8, 0, 23);
    const minute = parseScheduleNumber(rawMinute, 0, 0, 59);
    const dayOfMonth = parseScheduleNumber(form.dayOfMonth, 1, 1, 28);
    return buildCronExpression({
      frequency: ruleKey === COMPLETED_PROJECT_RULE_KEY ? 'monthly' : 'daily',
      dayOfMonth,
      hour,
      minute,
    });
  }, [form.dayOfMonth, form.time, ruleKey]);

  const handleNumberChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = () => {
    if (!configItem) return;

    const [rawHour, rawMinute] = String(form.time || '').split(':');
    const hour = parseScheduleNumber(rawHour, 8, 0, 23);
    const minute = parseScheduleNumber(rawMinute, 0, 0, 59);
    const baseSchedule = {
      ...(configItem.schedule_config || {}),
      timezone: 'America/Argentina/Buenos_Aires',
      hour,
      minute,
      cron_expression: schedulePreview,
    };

    let scheduleConfig = baseSchedule;
    let executionIntervalMinutes = 1440;

    if (ruleKey === COMPLETED_PROJECT_RULE_KEY) {
      scheduleConfig = {
        ...baseSchedule,
        frequency: 'monthly',
        day_of_month: parseScheduleNumber(form.dayOfMonth, 1, 1, 28),
      };
      executionIntervalMinutes = 43200;
    }

    if (ruleKey === COUPON_DUE_RULE_KEY) {
      scheduleConfig = {
        ...baseSchedule,
        frequency: 'daily',
        days_before_due: parseScheduleNumber(form.daysBeforeDue, 1, 0, 365),
      };
    }

    if (ruleKey === COUPON_COLLECTION_RULE_KEY) {
      scheduleConfig = {
        ...baseSchedule,
        frequency: 'daily',
        days_after_due: {
          '1': parseScheduleNumber(form.firstDueDelay, 1, 0, 365),
          '2': parseScheduleNumber(form.secondDueDelay, 1, 0, 365),
          '3': parseScheduleNumber(form.thirdDueDelay, 1, 0, 365),
        },
      };
    }

    updateConfig.mutate(
      {
        uuid: configItem.uuid,
        payload: {
          schedule_config: scheduleConfig,
          execution_interval_minutes: executionIntervalMinutes,
        },
      },
      {
        onSuccess: onClose,
      }
    );
  };

  const isSaving = updateConfig.isPending;

  return (
    <DialogContainer open={open} onClose={onClose} size="medium" minHeight="auto">
      <DialogTitle>Programación</DialogTitle>
      <DialogBody sx={{ display: 'block', p: 0 }}>
        <VerticalForm withBorder={false}>
          <Stack spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {configItem?.display_name || 'Notificación'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {configItem?.description || 'Sin descripción.'}
            </Typography>
          </Stack>
        </VerticalForm>

        <HorizontalForm withBorder={false} sx={{ gap: 1.5 }}>
          {ruleKey === COMPLETED_PROJECT_RULE_KEY && (
            <CustomTextField
              label="Día del mes"
              type="number"
              placeholder="Ej: 1"
              value={form.dayOfMonth}
              onChange={handleNumberChange('dayOfMonth')}
              inputProps={{ min: 1, max: 28 }}
              disabled={isSaving}
            />
          )}

          {ruleKey === COUPON_DUE_RULE_KEY && (
            <CustomTextField
              label="Días antes"
              type="number"
              placeholder="Ej: 1"
              value={form.daysBeforeDue}
              onChange={handleNumberChange('daysBeforeDue')}
              inputProps={{ min: 0, max: 365 }}
              disabled={isSaving}
            />
          )}

          {ruleKey === COUPON_COLLECTION_RULE_KEY && (
            <>
              <CustomTextField
                label="Días después primer vencimiento"
                type="number"
                placeholder="Ej: 1"
                value={form.firstDueDelay}
                onChange={handleNumberChange('firstDueDelay')}
                inputProps={{ min: 0, max: 365 }}
                disabled={isSaving}
              />
              <CustomTextField
                label="Días después segundo vencimiento"
                type="number"
                placeholder="Ej: 1"
                value={form.secondDueDelay}
                onChange={handleNumberChange('secondDueDelay')}
                inputProps={{ min: 0, max: 365 }}
                disabled={isSaving}
              />
              <CustomTextField
                label="Días después tercer vencimiento"
                type="number"
                placeholder="Ej: 1"
                value={form.thirdDueDelay}
                onChange={handleNumberChange('thirdDueDelay')}
                inputProps={{ min: 0, max: 365 }}
                disabled={isSaving}
              />
            </>
          )}

          <CustomTextField
            label="Hora"
            type="time"
            value={form.time}
            onChange={(event) => setForm((prev) => ({ ...prev, time: event.target.value }))}
            disabled={isSaving}
          />
        </HorizontalForm>
      </DialogBody>
      <DialogActions
        handleClose={onClose}
        handleSave={handleSave}
        cancelLabel="Cerrar"
        saveButtonProps={{
          disabled: !configItem,
          isLoading: isSaving,
        }}
      />
    </DialogContainer>
  );
};
